/**
 * SiteSection Component
 *
 * Site information for DC projects: name, location, climate zone, design temps.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

const CLIMATE_ZONES = [
  '1A - Very Hot Humid',
  '2A - Hot Humid',
  '2B - Hot Dry',
  '3A - Warm Humid',
  '3B - Warm Dry',
  '3C - Warm Marine',
  '4A - Mixed Humid',
  '4B - Mixed Dry',
  '4C - Mixed Marine',
  '5A - Cool Humid',
  '5B - Cool Dry',
  '6A - Cold Humid',
  '6B - Cold Dry',
  '7 - Very Cold',
  '8 - Subarctic',
];

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: vortxColors.white,
    '& fieldset': { borderColor: vortxColors.teal },
    '&:hover fieldset': { borderColor: vortxColors.gold },
    '&.Mui-focused fieldset': { borderColor: vortxColors.gold, borderWidth: 2 },
  },
  '& .MuiInputLabel-root': {
    color: vortxColors.stone,
    '&.Mui-focused': { color: vortxColors.gold },
  },
  '& .MuiFormHelperText-root': {
    color: vortxColors.stone,
  },
  '& .MuiInputAdornment-root': {
    color: vortxColors.gold,
  },
};

function SiteSection({ project, onUpdate }) {
  const [formData, setFormData] = useState({
    site_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    latitude: '',
    longitude: '',
    climate_zone: '',
    design_temp_dry_bulb_f: '',
    design_temp_wet_bulb_f: '',
  });

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        site_name: project.site_name || '',
        address: project.address || '',
        city: project.city || '',
        state: project.state || '',
        zip_code: project.zip_code || '',
        latitude: project.latitude || '',
        longitude: project.longitude || '',
        climate_zone: project.climate_zone || '',
        design_temp_dry_bulb_f: project.design_temp_dry_bulb_f || '',
        design_temp_wet_bulb_f: project.design_temp_wet_bulb_f || '',
      });
    }
  }, [project]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save site info:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: vortxTypography.heading,
          color: vortxColors.white,
          mb: 3,
        }}
      >
        Site Information
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Site Name"
            value={formData.site_name}
            onChange={handleChange('site_name')}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={handleChange('address')}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            value={formData.city}
            onChange={handleChange('city')}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="State"
            value={formData.state}
            onChange={handleChange('state')}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="ZIP Code"
            value={formData.zip_code}
            onChange={handleChange('zip_code')}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Latitude"
            type="number"
            value={formData.latitude}
            onChange={handleChange('latitude')}
            helperText="Used for weather data and solar irradiance"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Longitude"
            type="number"
            value={formData.longitude}
            onChange={handleChange('longitude')}
            helperText="Used for weather data and solar irradiance"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            select
            label="ASHRAE Climate Zone"
            value={formData.climate_zone}
            onChange={handleChange('climate_zone')}
            helperText="Determines cooling design parameters"
            sx={textFieldSx}
          >
            <MenuItem value="">Select zone...</MenuItem>
            {CLIMATE_ZONES.map((zone) => (
              <MenuItem key={zone} value={zone}>{zone}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Design Dry Bulb Temp"
            type="number"
            value={formData.design_temp_dry_bulb_f}
            onChange={handleChange('design_temp_dry_bulb_f')}
            helperText="ASHRAE 0.4% design dry bulb (F)"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Design Wet Bulb Temp"
            type="number"
            value={formData.design_temp_wet_bulb_f}
            onChange={handleChange('design_temp_wet_bulb_f')}
            helperText="ASHRAE 0.4% design wet bulb (F)"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  background: `linear-gradient(135deg, #d4a574 0%, ${vortxColors.gold} 100%)`,
                },
              }}
            >
              Save Changes
            </Button>
            {saveSuccess && <Alert severity="success">Changes saved successfully!</Alert>}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SiteSection;
